<template>
  <div>
    <v-menu v-model="menuOpened">
      <template #activator="{ props }">
        <v-tooltip location="bottom center">
          <template #activator="{ props: tooltip }">
            <div v-bind="props">
              <v-btn-icon
                icon="mdi-folder-open"
                v-bind="tooltip"
                :class="[{ 'px-5': $vuetify.display.sm }, { 'px-4': $vuetify.display.xs }]"
                data-testid="openItem.menu-button"
              >
              </v-btn-icon>
            </div>
          </template>
          <span>Statecharts</span>
        </v-tooltip>
      </template>

      <v-list density="compact">
        <recent-items
          @close-file-menu="closeMenu()"
          @open-statechart-wizard="
            () => {
              statechartWizardType = 'statecharts';
              openStatechartWizard = true;
            }
          "
        />
        <v-divider></v-divider>
        <v-menu close-on-content-click :open-on-hover="true" location="right top" min-width="220px">
          <template #activator="{ props }">
            <v-list-item v-bind="props" data-testid="openItem.new-statechart-menu-button" @click.stop>
              <div class="d-flex justify-space-between">
                <v-list-item-title>New Cloud Statechart</v-list-item-title>
                <v-icon>mdi-chevron-right</v-icon>
              </div>
            </v-list-item>
          </template>
          <v-list>
            <v-list-item data-testid="openItem.new-blank-statechart-button" @click="openCloudStatechart(null)">
              <v-list-item-title>Blank Statechart</v-list-item-title>
            </v-list-item>
            <v-list-item
              data-testid="openItem.examples-wizard-button"
              @click="
                statechartWizardType = 'examples';
                openStatechartWizard = true;
              "
            >
              <v-list-item-title>From Example</v-list-item-title>
            </v-list-item>
            <v-list-item @click="importFile()">
              <v-list-item-title>Import Local File</v-list-item-title>
            </v-list-item></v-list
          ></v-menu
        >
        <v-divider></v-divider>

        <v-list-item
          data-testid="openItem.my-statecharts-wizard-button"
          @click="
            statechartWizardType = 'statecharts';
            openStatechartWizard = true;
          "
        >
          <v-list-item-title>My Statecharts</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
    <statechart-wizard
      v-if="openStatechartWizard"
      :cancelable="wizardCancelable"
      :type="statechartWizardType"
      @cancel="resetWizard"
      @statechartSelected="openCloudStatechart"
    />
    <statechart-properties-dialog
      v-if="openTitleDialog"
      :new-model="newModel"
      @cancel="
        () => {
          newModel = null;
          openTitleDialog = false;
        }
      "
      @save="loadNew"
    ></statechart-properties-dialog>
  </div>
</template>

<script setup lang="ts">
import { MessageEventBus, WizardEventBus } from "../../../event-bus";
import RecentItems from "./RecentItems/RecentItems.vue";
import ModelLoader from "../../services/model-loader";
import StatechartWizard from "../../statecharts/StatechartWizard.vue";
import StatechartPropertiesDialog from "../../statecharts/StatechartPropertiesDialog.vue";
import { ProgressBarEventBus } from "../../../event-bus";
import { inject, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import { TransformerService } from "../../services/transformer-service";
import { loadStatechart } from "../../utils/loadStatechart";
import { RecentFile } from "../../services/storage/recent-items-service";
import { StatechartsService } from "../../services/statecharts/statecharts-service";
import { ModelData } from "../../services/statecharts/ModelData";
import { CodeSimulationService } from "../../services/simulation/code/simulation-service-code";
import { DirtyState } from "../../services/dirty-state";

type FileExtensionTypes = ".scm" | ".ysc" | ".sct";

const transformerService: TransformerService = inject<TransformerService>("transformerService");
const simulationControl: CodeSimulationService = inject<CodeSimulationService>("simulationControl");
const modelLoader: ModelLoader = inject<ModelLoader>("modelLoader");
const cloudStatechartService: StatechartsService = inject<StatechartsService>("cloudStatechartService");
const dirtyState: DirtyState = inject<DirtyState>("dirtyState");

const openStatechartWizard = ref(false);
const openTitleDialog = ref(false);
const newModel = ref<ModelData | null>(null);
const statechartWizardType = ref<"examples" | "statecharts">("examples");
const wizardCancelable = ref<boolean>(true);

const menuOpened = ref(false);

const forceSelectingStatechartHandler = () => {
  openStatechartWizard.value = true;
  wizardCancelable.value = false;
  statechartWizardType.value = "statecharts";
  nextTick(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  });
};

onMounted(() => {
  WizardEventBus.on("forceSelectingStatechart", forceSelectingStatechartHandler);
});

onBeforeUnmount(() => {
  WizardEventBus.off("forceSelectingStatechart", forceSelectingStatechartHandler);
});

function closeMenu() {
  menuOpened.value = false;
}

function resetWizard() {
  openStatechartWizard.value = false;
  wizardCancelable.value = true;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function openCloudStatechart(jsonText: any, stc?: RecentFile) {
  if (!stc) {
    newModel.value = jsonText;
    openTitleDialog.value = true;
  } else {
    loadStatechart(
      simulationControl,
      modelLoader,
      jsonText,
      stc.modelId,
      "'" + stc.model.title + "' opened!"
    );
    resetWizard();
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function loadNew(jsonText: any) {
  openTitleDialog.value = false;
  // a new state chart is always dirty until it has been saved
  dirtyState.setDirty(true);
  loadStatechart(simulationControl, modelLoader, jsonText, undefined);
  resetWizard();
}

function importFile() {
  // dirty until saved in the cloud
  dirtyState.setDirty(true);
  const fileExtList: FileExtensionTypes[] = [".ysc", ".sct", ".scm"];
  const input = document.createElement("input");
  input.setAttribute("type", "file");
  input.setAttribute("accept", fileExtList.join(", "));
  input.onchange = (evt) => {
    const selection = evt.target as HTMLInputElement;
    const validExtIndex = fileExtList.findIndex((ext) => selection.value.endsWith(ext));
    if (validExtIndex !== -1) {
      const file = selection.files[0];
      const reader = new FileReader();
      reader.onload = () => transformAndLoadFile(reader.result?.toString(), fileExtList[validExtIndex]);
      reader.readAsText(file);
    } else {
      MessageEventBus.emit("error", {
        id: "open-item",
        text: `Invalid file type. Please choose a ${fileExtList.join(" or a ")} file.`,
      });
    }
  };
  input.click();
}

async function transformAndLoadFile(content: string, fileExt: FileExtensionTypes) {
  let id: string;
  let model: string;

  ProgressBarEventBus.emit("startTask");

  try {
    if (fileExt === ".scm") {
      id = await cloudStatechartService.createModel(content, false).catch(() => {
        throw "Import failed. Possibly because the format of the content is invalid.";
      });
      model = await cloudStatechartService.getModel(id);
    } else {
      id = await transformerService.transform(content);
      model = await cloudStatechartService.getAsyncModel(id);
    }
    loadStatechart(
      simulationControl,
      modelLoader,
      model,
      id,
      "Model got successfully imported and opened!"
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    let errMsg = "Import failed.";
    if (typeof err === "string") errMsg = err;
    ProgressBarEventBus.emit("finishedTask");
    MessageEventBus.emit("error", {
      text: errMsg,
      id: "import-model",
    });
  }
}
</script>
